/**
 * OrderCartPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Row,
  Col,
  Title,
  Breadcrumb,
  Button,
  Table2,
  MobileTable,
  Text,
  Image,
  Modifier,
} from 'components';

import { stringToMoneyFormat } from 'utils';
import { Tooltip } from 'antd';
import {
  CloseOutlined,
  HeartFilled,
  HeartOutlined,
  PushpinFilled,
  PushpinOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { encrypt } from 'utils/Crypto';

const BREADCRUMB = [
  {
    key: 'shopping bag',
    title: 'SHOPPING BAG',
  },
  {
    key: 'order',
    title: 'ORDER',
  },
  {
    key: 'order confirmed',
    title: 'ORDER CONFIRMED',
  },
];

const ORDER_INFO = [
  '쿠폰/적립금은 주문서에서 사용 가능합니다.',
  '[상품준비중] 상태일 경우에만 주문 취소 가능합니다.',
  '[마이페이지 > 주문내역 상세페이지]에서 직접 취소하실 수 있습니다.',
  '쿠폰, 적립금 사용 금액을 제외한 실 결제 금액 기준으로 최종 산정됩니다.',
  '상품별로 적립금 지금 기준이 다를 수 있습니다. (상품 상세 페이지에서 확인 가능합니다.)',
];

const OrderCartPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    navigate,
    cart,
    type,
    selectedProduct,
    onCheckedList,
    onCheckedListAll,
    onCartNumber,
    onRemoveCart,
    onOrder,
    onUpdateCart,
    screenSize,
  } = props;

  /* ===== STATE ===== */
  // 총 주문금액, 총 배송비
  const [totalPrice, setTotalPrice] = React.useState({
    total_price: 0,
    total_delivery: 0,
  });

  /* ===== VARIABLE ===== */
  const styles = {
    titleLayout: {
      paddingBottom: 20,
      borderBottom: '4px solid var(--black-color)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
    },

    title: {
      fontSize: 28,
      fontWeight: 400,
      fontFamily: `${'Dela Gothic One'}, sans-serif`,
      lineHeight: '180%',
      textTransform: 'capitalize',
    },
    imageRatio: {
      width: 80,
      height: 'auto',
      aspectRatio: '4 / 5',
      objectFit: 'cover',
      // display: 'block',
      overflow: 'hidden',
    },
    topButton: {
      borderRadius: 0,
      width: screenSize >= 500 ? 80 : 60,
      height: screenSize >= 500 ? 32 : 25,
      fontSize: screenSize >= 500 ? 14 : 12,
      fontWeight: 400,
      color: 'var(--black-color)',
      padding: 0,
    },

    textButton: {
      color: 'var(--light-gray-color)',
      fontSize: 14,
      fontWeight: 700,
      textDecoration: 'underline',
      background: 'none',
      border: 'none',
    },

    amountButton: {
      width: 28,
      height: 28,
      borderRadius: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderColor: 'var(--light-gray-color)',
    },

    buyButton: {
      width: 100,
      height: 38,
      padding: 0,
      fontSize: 14,
      borderRadius: 0,
      marginTop: 8,
    },

    button: {
      width: '100%',
      minWidth: 120,
      height: screenSize >= 500 ? 80 : 50,
      lineHeight: screenSize >= 500 ? 'unset' : '130%',
      fontSize: screenSize >= 768 ? 28 : screenSize >= 500 ? 16 : 12,
      borderRadius: 0,
    },

    separator: {
      width: screenSize >= 768 ? 32 : screenSize >= 500 ? 20 : 15,
      height: screenSize >= 768 ? 32 : screenSize >= 500 ? 20 : 15,
      background: 'var(--black-color)',
      color: 'var(--white-color)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '50%',
      fontSize: screenSize >= 500 ? 24 : 15,
      lineHeight: '100%',
    },
    div: {
      width: '30%',
      textAlign: 'center',
    },

    circle: {
      width: screenSize >= 500 ? 20 : 15,
      height: screenSize >= 500 ? 20 : 15,
      background: 'var(--black-color)',
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'var(--white-color)',
      lineHeight: '100%',
    },

    tableButtonWrap: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
    },
    tableButton: {
      fontSize: 20,
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: 'var(--black-color)',
    },
  };

  /* ===== FUNCTION ===== */
  /** 장바구니에 들어가있는 상품 갯수를 리턴해주는 함수 */
  const itemCountFun = (key) => {
    const itemCount = cartList[key]
      .flatMap(item => item.items.map(value => value.num))
      .reduce((acc, cur) => acc + cur, 0)
    return itemCount
  }
  /* ===== HOOKS ===== */
  /**
   * 장바구니 테이블 목록 정제
   * --
   */
  const cartList = React.useMemo(() => {
    const newCartList = cart.map((cartItem, index) => ({
      key: `${cartItem.product_id}-${index}`,
      product_name: cartItem.product_name,
      brand: cartItem.brand?.shop_information_name,
      option: cartItem.product_option
        ? cartItem.product_option
          .map(
            (cur) => cur.option.map((op) => op.option_name).join(' / ') // 각 옵션 그룹의 옵션 이름을 합침
          )
          .join(' / ') // 옵션 그룹들을 합침
        : '',
      src: cartItem.src,
      num: cartItem.cart_num,
      price: cartItem.price + cartItem.option_price,
      finalPrice: cartItem.finalPrice,
      discount_rate: cartItem.discount_rate,
      discount_type: cartItem.discount_type,
      delivery_fee: cartItem?.forwarding_location?.delivery_price,
      delivery_free: cartItem?.forwarding_location?.free_delivery_price,
      forwardingKey: cartItem?.forwarding_location?.forwarding_location_id,
      cart_type: cartItem.cart_type,
      data: cartItem,
      isWish: cartItem.isWish,
      isFixed: cartItem.is_fixed === 'Y' ? cartItem.cart_id : null,
      regular_cycle: cartItem.regular_cycle,
      max_regular_delivery_count: cartItem.max_regular_delivery_count,
      isSoldOut: cartItem.is_sold_out,
      disabled: cartItem.is_sold_out === 'Y' ? true : false,
      forwarding: cartItem?.forwarding_location,
    }));

    return newCartList.reduce(
      (acc, cart) => {
        const {
          cart_type,
          forwardingKey,
          delivery_fee,
          delivery_free,
          forwarding,
        } = cart;

        // cart_type이 존재하지 않으면 무시
        if (!cart_type) return acc;

        // cart_type 그룹이 없으면 초기화
        if (!acc[cart_type]) {
          acc[cart_type] = [];
        }

        // forwarding 값을 기준으로 같은 그룹 찾기
        let forwardingGroup = acc[cart_type].find(
          (group) => group.forwardingKey === forwardingKey
        );

        // forwarding 그룹이 없다면 새 배열 생성
        if (!forwardingGroup) {
          forwardingGroup = {
            forwardingKey,
            delivery_fee,
            delivery_free,
            forwarding,
            items: [],
          };
          acc[cart_type].push(forwardingGroup);
        }

        // 동일한 forwarding 그룹에 cart 추가
        forwardingGroup.items.push(cart);

        return acc;
      },
      { DELIVERY: [], STORE_PICKUP: [], RENTAL: [], SUBSCRIPTION: [] }
    );
  }, [cart]);

  /**
   * 탭 아이템 정제
   * --
   */
  const tapItem = React.useMemo(() => {
    return [
      {
        key: 'DELIVERY',
        title: (
          <>
            택배배송 <span>{itemCountFun('DELIVERY')}</span>
          </>
        ),
        onClick: () => navigate('/order/cart?type=delivery'),
      },
      {
        key: 'STORE_PICKUP',
        title: (
          <>
            매장픽업 <span>{itemCountFun('STORE_PICKUP')}</span>
          </>
        ),
        onClick: () => navigate('/order/cart?type=store_pickup'),
      },
      // {
      //   key: 'RENTAL',
      //   title: (
      //     <>
      //       렌탈 <span>{cartList['RENTAL'].length}</span>
      //     </>
      //   ),
      //   onClick: () => navigate('/order/cart?type=rental'),
      // },
      {
        key: 'SUBSCRIPTION',
        title: (
          <>
            정기배송 <span>{itemCountFun('SUBSCRIPTION')}</span>
          </>
        ),
        onClick: () => navigate('/order/cart?type=subscription'),
      },
    ];
  }, [cartList, navigate]);

  /**
   * 상품 합계 계산
   * --
   */
  React.useEffect(() => {
    const selected = Object.values(selectedProduct);

    if (!selected.length)
      return setTotalPrice({
        total_price: 0,
        total_delivery: 0,
      });
    /**
     * 픽업 상품과 배송상품의 배달비 측정
     */
    const selectedData = selected.reduce(
      (acc, cur) => {
        const forwardingTotal = cur.reduce((total, item) => {
          return (total += item.finalPrice * item.num);
        }, 0);
        //픽업 상품일 경우 배송비 제외
        const checkCartType = selected[selected.length - 1]?.find((item) => item.cart_type !== "STORE_PICKUP");
        const [item] = cur;

        if (!item) return acc;

        const { delivery_fee, delivery_free } = item;

        const finalDeliveryFee =
          checkCartType ? forwardingTotal < delivery_free ? delivery_fee : 0 : 0;

        acc['total_price'] += forwardingTotal;
        acc['total_delivery'] += finalDeliveryFee;

        return acc;
      },
      { total_price: 0, total_delivery: 0 }
    );

    setTotalPrice(selectedData);
  }, [selectedProduct]);

  /* ===== RENDER ===== */
  return (
    <Content
      maxWidth={1820}
      padding={screenSize >= 768 ? '130px 0px' : '80px 0'}
      className="ordercart"
    >
      <Row gutter={16}>
        <Col x={1} md={2}></Col>
        <Col x={22} md={20}>
          <div
            style={{
              ...styles.titleLayout,
            }}
          >
            <Title style={{ ...styles.title }}>shopping bag</Title>

            <Breadcrumb
              items={BREADCRUMB}
              separator={'>'}
              itemStyle={{
                color: 'var(--light-gray-color)',
                fontFamily: `${'Dela Gothic One'}, sans-serif`,
                fontSize: screenSize >= 500 ? 'unset' : 14,
              }}
              activeKey={'shopping bag'}
              activeColor={'var(--black-color)'}
              separatorStyle={{ color: 'var(--light-gray-color)' }}
              style={{
                flexDirection: screenSize >= 768 ? 'unset' : 'column',
                alignItems: screenSize >= 768 ? 'center' : 'start',
                marginTop: screenSize >= 768 ? 0 : 20,
                rowGap: screenSize >= 768 ? 0 : 10,
              }}
            />
          </div>

          <div
            style={{
              marginTop: screenSize >= 768 ? 80 : screenSize >= 500 ? 40 : 20,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              rowGap: 20,
            }}
          >
            <Breadcrumb
              items={tapItem}
              separator={screenSize >= 768 ? '|' : ''}
              itemStyle={{
                color: 'var(--light-gray-color)',
                fontSize: screenSize >= 768 ? 28 : screenSize >= 500 ? 20 : 16,
                fontWeight: 700,
              }}
              activeKey={type}
              activeColor={'var(--black-color)'}
              padding={28}
              separatorStyle={{
                color: 'var(--light-gray-color)',
                fontWeight: 400,
              }}
              style={{
                //flexDirection: screenSize >= 768 ? 'unset' : 'column',
                alignItems: 'center',
                rowGap: 10,
                width: '100%',
                paddingBottom:
                  screenSize >= 1300 ? 'none' : screenSize >= 768 ? 80 : 40,
                borderBottom:
                  screenSize >= 1300 ? 'none' : '3px solid var(--black-color)',
              }}
            />

            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
                //order: screenSize >= 500 ? 'unset' : -1,
                paddingTop: screenSize >= 500 ? 0 : 20,
                borderTop:
                  screenSize >= 500 ? 'none' : '2px solid var(--black-color)',
              }}
            >
              <Button
                type="secondary"
                style={{ ...styles.topButton }}
                onClick={() => onRemoveCart('select')}
              >
                선택 삭제
              </Button>
              <Button
                type="secondary"
                style={{
                  ...styles.topButton,
                  marginLeft: screenSize >= 768 ? 16 : 10,
                }}
                onClick={() => onRemoveCart('all')}
              >
                전체 삭제
              </Button>
            </div>
          </div>

          {cartList[type]?.length ? (
            cartList[type].map((cart, index) => {
              const totalPrice = selectedProduct[index]?.reduce((acc, cur) => {
                return (acc += cur.finalPrice * cur.num);
              }, 0);

              const isSellable = cart?.items?.filter(
                (_item) => _item.isSoldOut === 'N'
              );

              return (
                <div
                  key={`table-${index}`}
                  style={{ marginBottom: screenSize >= 768 ? 100 : 80 }}
                >
                  {screenSize >= 1300 ? (
                    <Table2
                      data={cart.items}
                      isChecked
                      checkedAll={
                        selectedProduct[index]?.length === isSellable?.length
                      }
                      checkedList={selectedProduct[index]}
                      onCheckedAll={() => onCheckedListAll(index, isSellable)}
                      onChecked={(_, d) => onCheckedList(index, d)}
                      bodyRowStyle={{ padding: '30px 47px' }}
                      columns={[
                        {
                          title: '상품정보',
                          key: 'product_name',
                          render: (_, state) => (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 24,
                                width: 400,
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                navigate(
                                  `/products/${encodeURIComponent(
                                    encrypt(state.data.product_id)
                                  )}`
                                )
                              }
                            >
                              <Image
                                src={state.src}
                                alt={state.product_name}
                                //width={80}
                                //height={80}
                                style={styles.imageRatio}
                              />
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'baseline',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Text
                                  size={14}
                                  weight={700}
                                  style={{ lineHeight: '180%' }}
                                >
                                  {state.brand}
                                </Text>
                                <Text size={16} style={{ lineHeight: '180%' }}>
                                  {state.product_name}
                                </Text>
                                {state.option ? (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      width: '100%',
                                      gap: 10,
                                    }}
                                  >
                                    <Text
                                      size={14}
                                      color={'var(--light-gray-color)'}
                                    >
                                      {state.option}
                                    </Text>
                                  </div>
                                ) : null}
                                {state.regular_cycle ? (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      width: '100%',
                                      gap: 5,
                                    }}
                                  >
                                    <Text
                                      size={14}
                                      color={'var(--light-gray-color)'}
                                    >
                                      배송주기
                                    </Text>
                                    <Text size={14} color={'#D33444'}>
                                      {state.regular_cycle.split('-')[0]}
                                      {state.regular_cycle.split('-')[1] === 'W'
                                        ? '주'
                                        : '개월'}
                                    </Text>
                                    <Text
                                      size={14}
                                      color={'var(--light-gray-color)'}
                                    >
                                      /
                                    </Text>
                                    <Text
                                      size={14}
                                      color={'var(--light-gray-color)'}
                                    >
                                      배송횟수
                                    </Text>
                                    <Text size={14} color={'#D33444'}>
                                      {state.max_regular_delivery_count === 0
                                        ? '제한없음'
                                        : `${state.max_regular_delivery_count}회`}
                                    </Text>
                                    {console.log('state ===>', state)}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          ),
                          style: {
                            padding: 21,
                          },
                        },
                        {
                          title: '수량',
                          key: 'num',
                          render: (item, state) => (
                            <div>
                              {state.isSoldOut === 'Y' ? (
                                <Text color={'#999999'}>품절</Text>
                              ) : (
                                <Modifier
                                  init={item}
                                  minNum={1}
                                  maxNum={99}
                                  layoutStyle={{ height: 36 }}
                                  readOnly
                                  unit={null}
                                  onIncrease={() =>
                                    onCartNumber(
                                      { ...state, indexKey: index },
                                      item + 1
                                    )
                                  }
                                  onDecrease={() =>
                                    onCartNumber(
                                      { ...state, indexKey: index },
                                      item - 1
                                    )
                                  }
                                />
                              )}
                            </div>
                          ),
                          style: {
                            padding: 21,
                          },
                        },
                        {
                          title: '주문금액',
                          key: 'price',
                          render: (_, state) =>
                            state.isSoldOut === 'Y' ? (
                              <div>
                                <Text size={16} weight={700} color={'#999999'}>
                                  {stringToMoneyFormat(state.price)}
                                  <span
                                    style={{ fontWeight: 400, paddingLeft: 4 }}
                                  >
                                    원
                                  </span>
                                </Text>
                              </div>
                            ) : (
                              <div>
                                {state.discount_rate ? (
                                  <Text
                                    size={14}
                                    color={'var(--light-gray-color)'}
                                    style={{ textDecoration: 'line-through' }}
                                  >
                                    {stringToMoneyFormat(
                                      state.price * state.num
                                    )}
                                  </Text>
                                ) : null}
                                <Text size={16} weight={700}>
                                  {stringToMoneyFormat(
                                    state.finalPrice * state.num
                                  )}
                                  <span
                                    style={{ fontWeight: 400, paddingLeft: 4 }}
                                  >
                                    원
                                  </span>
                                </Text>
                              </div>
                            ),
                          style: {
                            padding: 21,
                          },
                        },
                        {
                          title: '선택',
                          key: '',
                          render: (_, state) => (
                            <div style={{ ...styles.tableButtonWrap }}>
                              <button
                                style={{ ...styles.tableButton }}
                                onClick={() =>
                                  state.isFixed
                                    ? onUpdateCart(
                                      'FIX_DELETE',
                                      state.data.cart_id
                                    )
                                    : onUpdateCart(
                                      'FIX_ADD',
                                      state.data.cart_id
                                    )
                                }
                              >
                                {state.isFixed ? (
                                  <PushpinFilled />
                                ) : (
                                  <PushpinOutlined />
                                )}
                              </button>
                              <button
                                style={{ ...styles.tableButton }}
                                onClick={() =>
                                  state.isWish
                                    ? onUpdateCart('WISH_DELETE', state.isWish)
                                    : onUpdateCart(
                                      'WISH_ADD',
                                      state.data.product_id
                                    )
                                }
                              >
                                {state.isWish ? (
                                  <HeartFilled style={{ color: '#D33444' }} />
                                ) : (
                                  <HeartOutlined color="#111111" />
                                )}
                              </button>
                              <button
                                style={{ ...styles.tableButton }}
                                onClick={() =>
                                  onRemoveCart('single', state?.data?.cart_id)
                                }
                              >
                                <CloseOutlined />
                              </button>
                            </div>
                          ),
                        },
                      ]}
                      layoutStyle={{ marginTop: screenSize >= 500 ? 32 : 20 }}
                      headerStyle={{
                        background: 'transparent',
                        fontSize:
                          screenSize >= 768 ? 18 : screenSize >= 500 ? 16 : 15,
                        fontWeight: 700,
                        color: 'var(--black-color)',
                        borderTop: '2px solid var(--black-color)',
                        padding: 16,
                      }}
                      bordered={false}
                      hovered={false}
                      minHeight={cart.length ? 'auto' : 200}
                    />
                  ) : (
                    <MobileTable
                      data={cart.items}
                      isChecked
                      checkedList={selectedProduct[index]}
                      onChecked={(e, d) => onCheckedList(index, d)}
                      onCartNumber={(state, value) =>
                        onCartNumber({ ...state, indexKey: index }, value)
                      }
                      onRemoveCart={onRemoveCart}
                      onUpdateCart={onUpdateCart}
                      navigate={navigate}
                      screenSize={screenSize}
                    />
                  )}

                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: screenSize >= 500 ? 20 : 15,
                      padding: screenSize >= 500 ? 30 : '20px 15%',
                      borderBottom: '1px solid rgba(112, 115, 124, 0.2)',
                      position: 'relative',
                      top: 0,
                      left: 0,
                    }}
                  >
                    <div>
                      <Text size={screenSize >= 500 ? 18 : 15} weight={500}>
                        상품금액 {stringToMoneyFormat(totalPrice, '원')}
                      </Text>
                    </div>
                    <div style={{ ...styles.circle }}>+</div>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                    >
                      <Text size={screenSize >= 500 ? 18 : 15} weight={500}>
                        배송비{' '}
                        {selectedProduct[index]?.length
                          ? selectedProduct[index]?.find((itme) => itme.cart_type !== "STORE_PICKUP") ?
                            stringToMoneyFormat(
                              totalPrice >= cart.delivery_free
                                ? 0
                                : cart.delivery_fee,
                              '원'
                            )
                            : '0원' : '0원'}
                      </Text>
                      <Tooltip
                        title={
                          cart.delivery_fee === 0 ? (
                            type === 'STORE_PICKUP' ? (
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 12,
                                }}
                              >
                                <Text size={14} weight={700} color={'#FFFFFF'}>
                                  픽업지
                                </Text>
                                <div>
                                  <Text
                                    size={14}
                                    weight={500}
                                    color={'#FFFFFF'}
                                  >
                                    {cart?.forwarding?.forwarding_name}
                                  </Text>
                                  <div>
                                    <Text size={12} color={'#F4F4F4'}>
                                      {cart?.forwarding?.forwarding_address}
                                    </Text>
                                    <Text size={12} color={'#F4F4F4'}>
                                      {
                                        cart?.forwarding
                                          ?.forwarding_address_detail
                                      }
                                    </Text>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              '무료배송'
                            )
                          ) : (
                            `${stringToMoneyFormat(
                              cart.delivery_free
                            )}원 이상 주문 또는 픽업시 무료배송`
                          )
                        }
                      >
                        <QuestionCircleOutlined style={{ fontSize: 16 }} />
                      </Tooltip>
                    </div>
                    <div style={{ ...styles.circle }}>=</div>
                    <div>
                      <Text size={screenSize >= 500 ? 18 : 15} weight={700}>
                        합계
                        {selectedProduct[index]?.length
                          ? selectedProduct[index].find((item) => item.cart_type !== "STORE_PICKUP")
                            ? stringToMoneyFormat(
                              totalPrice +
                              (totalPrice >= cart.delivery_free
                                ? 0
                                : cart.delivery_fee),
                              '원'
                            )
                            : stringToMoneyFormat(totalPrice, '원') : '0원'}
                      </Text>
                    </div>
                  </div>
                </div>
              );
            })
          ) : screenSize >= 1300 ? (
            <Table2
              data={[]}
              isChecked
              bodyRowStyle={{ padding: '30px 47px' }}
              columns={[
                {
                  title: '상품정보',
                  key: 'product_name',
                  render: (_, state) => (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 24,
                        width: 400,
                      }}
                    >
                      <Image
                        src={state.src}
                        alt={state.product_name}
                        width={80}
                        height={80}
                      />
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text
                          size={14}
                          weight={700}
                          style={{ lineHeight: '180%' }}
                        >
                          {state.brand}
                        </Text>
                        <Text size={16} style={{ lineHeight: '180%' }}>
                          {state.product_name}
                        </Text>
                        {state.option ? (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              gap: 10,
                            }}
                          >
                            <Text size={14} color={'var(--light-gray-color)'}>
                              {state.option}
                            </Text>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ),
                  style: {
                    padding: 21,
                  },
                },
                {
                  title: '수량',
                  key: 'num',
                  render: (item, state) => (
                    <div>
                      <Modifier
                        init={item}
                        layoutStyle={{ height: 36 }}
                        readOnly
                        unit={null}
                        onIncrease={() => onCartNumber(state.data, item + 1)}
                      />
                    </div>
                  ),
                  style: {
                    padding: 21,
                  },
                },
                {
                  title: '주문금액',
                  key: 'price',
                  render: (_, state) => (
                    <div>
                      {state.discount_rate ? (
                        <Text
                          size={14}
                          color={'var(--light-gray-color)'}
                          style={{ textDecoration: 'line-through' }}
                        >
                          {stringToMoneyFormat(state.price)}
                        </Text>
                      ) : null}
                      <Text size={16} weight={700}>
                        {stringToMoneyFormat(state.finalPrice)}
                        <span style={{ fontWeight: 400, paddingLeft: 4 }}>
                          원
                        </span>
                      </Text>
                    </div>
                  ),
                  style: {
                    padding: 21,
                  },
                },
                {
                  title: '선택',
                  key: '',
                  render: (_, state) => <div></div>,
                },
              ]}
              layoutStyle={{ marginTop: 32 }}
              headerStyle={{
                background: 'transparent',
                fontSize: screenSize >= 768 ? 18 : screenSize >= 500 ? 16 : 15,
                fontWeight: 700,
                color: 'var(--black-color)',
                borderTop: '2px solid var(--black-color)',
                padding: 16,
              }}
              bordered={false}
              hovered={false}
              minHeight={cart.length ? 'auto' : 200}
            />
          ) : (
            <MobileTable
              data={[]}
              isChecked
              checkedList={[]}
              onChecked={() => { }}
              onCartNumber={() => { }}
              onRemoveCart={() => { }}
              onUpdateCart={() => { }}
              navigate={navigate}
            />
          )}

          <ul style={{ marginTop: 20 }}>
            {ORDER_INFO.map((_item, _index) => (
              <li key={`order_info_item_${_index}`}>
                <Text
                  size={14}
                  color={'var(--dark-gray-color)'}
                  style={{ lineHeight: '200%', webkitLineClamp: 'unset' }}
                >
                  <span>•</span> {_item}
                </Text>
              </li>
            ))}
          </ul>

          <div style={{ marginTop: screenSize >= 768 ? 100 : 80 }}>
            <ul
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                width: '100%',
                padding: '16px 0px',
                borderTop: '2px solid var(--black-color)',
                borderBottom: '1px solid var(--light-gray-color)',
              }}
            >
              <li style={{ ...styles.div }}>
                <Text size={screenSize >= 768 ? 18 : 16} weight={700}>
                  총 주문금액
                </Text>
              </li>
              <li
                style={{
                  ...styles.separator,
                  opacity: 0,
                }}
              ></li>
              <li style={{ ...styles.div }}>
                <Text size={screenSize >= 768 ? 18 : 16} weight={700}>
                  총 배송비
                </Text>
              </li>
              <li
                style={{
                  ...styles.separator,
                  opacity: 0,
                }}
              ></li>
              <li style={{ ...styles.div }}>
                <Text size={screenSize >= 768 ? 18 : 16} weight={700}>
                  총 결제금액
                </Text>
              </li>
            </ul>
            <ul
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                width: '100%',
                borderBottom: '1px solid var(--black-color)',
                padding: '30px 0px',
              }}
            >
              <li style={{ ...styles.div }}>
                <Text
                  size={screenSize >= 768 ? 28 : screenSize >= 500 ? 18 : 14}
                  weight={700}
                >
                  {stringToMoneyFormat(totalPrice.total_price)}
                  <span
                    style={{
                      fontSize:
                        screenSize >= 768 ? 24 : screenSize >= 500 ? 16 : 14,
                      fontWeight: 400,
                      color: 'var(--black-color)',
                    }}
                  >
                    원
                  </span>
                </Text>
              </li>
              <li
                style={{
                  ...styles.separator,
                }}
              >
                +
              </li>
              <li style={{ ...styles.div }}>
                <Text
                  size={screenSize >= 768 ? 28 : screenSize >= 500 ? 18 : 14}
                  weight={700}
                >
                  {stringToMoneyFormat(totalPrice.total_delivery)}
                  <span
                    style={{
                      fontSize:
                        screenSize >= 768 ? 24 : screenSize >= 500 ? 16 : 14,
                      fontWeight: 400,
                      color: 'var(--black-color)',
                    }}
                  >
                    원
                  </span>
                </Text>
              </li>
              <li
                style={{
                  ...styles.separator,
                }}
              >
                =
              </li>
              <li style={{ ...styles.div }}>
                <Text
                  size={screenSize >= 768 ? 28 : screenSize >= 500 ? 18 : 14}
                  weight={700}
                >
                  {stringToMoneyFormat(
                    totalPrice.total_price + totalPrice.total_delivery
                  )}
                  <span
                    style={{
                      fontSize:
                        screenSize >= 768 ? 24 : screenSize >= 500 ? 16 : 14,
                      fontWeight: 400,
                      color: 'var(--black-color)',
                    }}
                  >
                    원
                  </span>
                </Text>
              </li>
            </ul>
          </div>
        </Col>
        <Col x={1} md={2}></Col>
      </Row>

      <Row gutter={16} style={{ marginTop: screenSize >= 768 ? 100 : 80 }}>
        <Col x={2} md={6}></Col>
        <Col x={10} md={6}>
          <Button
            type="secondary"
            className="dela-gothic"
            style={{ ...styles.button }}
            onClick={() => navigate('/products')}
          >
            Continue Shopping
          </Button>
        </Col>
        <Col x={10} md={6}>
          <Button
            className="dela-gothic"
            style={{ ...styles.button }}
            onClick={() => onOrder()}
          >
            Order
          </Button>
        </Col>
        <Col x={2} md={6}></Col>
      </Row>
    </Content>
  );
};

export default OrderCartPresenter;
